import React, { useState, useCallback } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { QualityMetricsVisualization } from './QualityMetricsVisualization';
import { QualityImprovementPanel } from './QualityImprovementPanel';
import { QualityTrendingAnalytics } from './QualityTrendingAnalytics';
import { WorkflowQualityIntegration } from './WorkflowQualityIntegration';
import { QualityAssuranceReporting } from './QualityAssuranceReporting';
import { QualityPolicyManager } from './QualityPolicyManager';
import { useAdvancedContentQuality } from '../../hooks/useAdvancedContentQuality';
import type {
  AdvancedContentQualityDashboardProps,
  QualityMetricsData,
  QualitySuggestion
} from '../../types/advanced_content_quality_ui';

// Dashboard view modes
type DashboardView = 'overview' | 'metrics' | 'recommendations' | 'trends' | 'workflow' | 'reporting' | 'policy';

// Dashboard tabs configuration
const DASHBOARD_TABS = [
  { id: 'overview', label: 'Overview', icon: '📊' },
  { id: 'metrics', label: 'Quality Metrics', icon: '📈' },
  { id: 'recommendations', label: 'Recommendations', icon: '💡' },
  { id: 'trends', label: 'Trending Analytics', icon: '📉' },
  { id: 'workflow', label: 'Workflow', icon: '🔄' },
  { id: 'reporting', label: 'Reports', icon: '📋' },
  { id: 'policy', label: 'Policy', icon: '⚙️' }
] as const;

export const AdvancedContentQualityDashboard: React.FC<AdvancedContentQualityDashboardProps> = ({
  contentId,
  contentType,
  realTimeUpdates = true,
  compactView = false,
  onQualityUpdate,
  onRecommendationApplied
}) => {
  // State management
  const [activeView, setActiveView] = useState<DashboardView>('overview');
  const [refreshing, setRefreshing] = useState(false);

  // Advanced content quality hook
  const {
    qualityData,
    loading,
    error,
    refreshQuality,
    updateQualityMetrics,
    applyRecommendation,
    configureWorkflow,
    generateReport,
    updatePolicy
  } = useAdvancedContentQuality({
    contentId,
    contentType,
    realTimeUpdates,
    autoRefresh: true,
    refreshInterval: 30000
  });

  // Handle manual refresh
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refreshQuality();
    } catch (err) {
      console.error('Error refreshing quality data:', err);
    } finally {
      setRefreshing(false);
    }
  }, [refreshQuality]);

  // Handle quality metric updates
  const handleQualityUpdate = useCallback((metrics: QualityMetricsData) => {
    updateQualityMetrics(metrics);
    onQualityUpdate?.(metrics);
  }, [updateQualityMetrics, onQualityUpdate]);

  // Handle recommendation application
  const handleRecommendationApply = useCallback(async (recommendation: QualitySuggestion) => {
    try {
      await applyRecommendation(recommendation);
      onRecommendationApplied?.(recommendation);
    } catch (err) {
      console.error('Error applying recommendation:', err);
    }
  }, [applyRecommendation, onRecommendationApplied]);

  // Calculate overall quality status
  const getQualityStatus = () => {
    if (!qualityData?.qualityMetrics) return 'unknown';
    
    const { overallQualityScore } = qualityData.qualityMetrics;
    const score = overallQualityScore.compositeScore;
    
    if (score >= 90) return 'excellent';
    if (score >= 80) return 'good';
    if (score >= 70) return 'fair';
    if (score >= 60) return 'needs-improvement';
    return 'poor';
  };

  // Get quality status styling
  const getQualityStatusStyle = (status: string) => {
    const baseClasses = 'px-3 py-1 rounded-full text-sm font-medium';
    switch (status) {
      case 'excellent':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'good':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'fair':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'needs-improvement':
        return `${baseClasses} bg-orange-100 text-orange-800`;
      case 'poor':
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  // Render loading state
  if (loading && !qualityData) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-gray-600">Loading advanced quality dashboard...</span>
      </div>
    );
  }

  // Render error state
  if (error && !qualityData) {
    return (
      <Card className="p-6 border-red-200 bg-red-50">
        <div className="flex items-center space-x-3">
          <span className="text-red-500 text-xl">⚠️</span>
          <div>
            <h3 className="text-red-800 font-semibold">Error Loading Quality Dashboard</h3>
            <p className="text-red-600 text-sm mt-1">{error}</p>
            <Button
              onClick={handleRefresh}
              variant="secondary"
              size="small"
              className="mt-3 border-red-300 text-red-700 hover:bg-red-100"
              disabled={refreshing}
            >
              {refreshing ? 'Retrying...' : 'Retry'}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Render overview dashboard
  const renderOverviewDashboard = () => {
    if (!qualityData) return null;

    const qualityStatus = getQualityStatus();
    const { qualityMetrics, improvementRecommendations } = qualityData;
    const criticalRecommendations = improvementRecommendations.automatedSuggestions.filter(
      s => s.severity === 'critical'
    );

    return (
      <div className="space-y-6">
        {/* Quality Status Header */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Content Quality Overview</h2>
              <p className="text-gray-600 mt-1">Comprehensive quality assessment and management</p>
            </div>
            <div className="text-right">
              <div className={getQualityStatusStyle(qualityStatus)}>
                {qualityStatus.replace('-', ' ').toUpperCase()}
              </div>
              <div className="text-2xl font-bold text-gray-900 mt-2">
                {qualityMetrics.overallQualityScore.compositeScore}%
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <span className="text-blue-600 text-lg">🎯</span>
              </div>
              <div>
                <div className="text-sm text-gray-600">Clarity Score</div>
                <div className="text-xl font-semibold text-gray-900">
                  {qualityMetrics.clarityScoring.score}%
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <span className="text-green-600 text-lg">🔗</span>
              </div>
              <div>
                <div className="text-sm text-gray-600">Coherence Score</div>
                <div className="text-xl font-semibold text-gray-900">
                  {qualityMetrics.coherenceScoring.score}%
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <span className="text-purple-600 text-lg">💥</span>
              </div>
              <div>
                <div className="text-sm text-gray-600">Impact Score</div>
                <div className="text-xl font-semibold text-gray-900">
                  {qualityMetrics.impactScoring.score}%
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <span className="text-yellow-600 text-lg">⚡</span>
              </div>
              <div>
                <div className="text-sm text-gray-600">Recommendations</div>
                <div className="text-xl font-semibold text-gray-900">
                  {improvementRecommendations.automatedSuggestions.length}
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Critical Recommendations Alert */}
        {criticalRecommendations.length > 0 && (
          <Card className="p-4 border-orange-200 bg-orange-50">
            <div className="flex items-center space-x-3">
              <span className="text-orange-500 text-xl">🚨</span>
              <div className="flex-1">
                <h3 className="text-orange-800 font-semibold">Critical Quality Issues Detected</h3>
                <p className="text-orange-700 text-sm mt-1">
                  {criticalRecommendations.length} critical quality issue{criticalRecommendations.length > 1 ? 's' : ''} requiring immediate attention
                </p>
              </div>
              <Button
                onClick={() => setActiveView('recommendations')}
                size="small"
                className="bg-orange-600 hover:bg-orange-700"
              >
                View Issues
              </Button>
            </div>
          </Card>
        )}

        {/* Quick Action Panels */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quality Metrics Summary</h3>
            <QualityMetricsVisualization
              metrics={qualityMetrics}
              showTrends={false}
              interactive={false}
              onMetricClick={(metric: string) => setActiveView('metrics')}
            />
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Recommendations</h3>
            <QualityImprovementPanel
              recommendations={improvementRecommendations}
              allowAutoApply={true}
              showPrioritization={true}
              onRecommendationApply={handleRecommendationApply}
            />
          </Card>
        </div>
      </div>
    );
  };

  // Render active view content
  const renderActiveView = () => {
    if (!qualityData) return null;

    switch (activeView) {
      case 'overview':
        return renderOverviewDashboard();
      
      case 'metrics':
        return (
          <QualityMetricsVisualization
            metrics={qualityData.qualityMetrics}
            showTrends={true}
            interactive={true}
            onMetricClick={(metric: string) => console.log('Metric clicked:', metric)}
          />
        );
      
      case 'recommendations':
        return (
          <QualityImprovementPanel
            recommendations={qualityData.improvementRecommendations}
            allowAutoApply={true}
            showPrioritization={true}
            onRecommendationApply={handleRecommendationApply}
            onRecommendationReject={(rec: { id?: string; title?: string }, reason: string) => console.log('Recommendation rejected:', rec, reason)}
          />
        );
      
      case 'trends':
        return (
          <QualityTrendingAnalytics
            trendingData={qualityData.trendingAnalytics}
            showPredictions={true}
            comparisonMode="both"
            onTrendClick={(point: { date?: string; value?: number }) => console.log('Trend point clicked:', point)}
          />
        );
      
      case 'workflow':
        return (
          <WorkflowQualityIntegration
            workflowData={qualityData.workflowIntegration}
            contentId={contentId || ''}
            allowConfiguration={true}
            showAutomation={true}
            onWorkflowUpdate={configureWorkflow}
          />
        );
      
      case 'reporting':
        return (
          <QualityAssuranceReporting
            reportingData={qualityData.reportingSystem}
            reportType="operational"
            exportOptions={['pdf', 'excel', 'json']}
            onReportGenerate={generateReport}
          />
        );
      
      case 'policy':
        return (
          <QualityPolicyManager
            policyData={qualityData.policyManagement}
            editMode={true}
            showTemplates={true}
            onPolicyUpdate={updatePolicy}
            onThresholdChange={(thresholds: Record<string, number>) => updatePolicy({ qualityThresholds: thresholds })}
          />
        );
      
      default:
        return renderOverviewDashboard();
    }
  };

  return (
    <div className={`advanced-content-quality-dashboard ${compactView ? 'compact' : ''}`}>
      {/* Dashboard Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Advanced Content Quality Dashboard</h1>
            <p className="text-gray-600 mt-1">
              Comprehensive quality management and optimization for {contentType || 'content'}
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            {realTimeUpdates && (
              <div className="flex items-center space-x-2 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span>Live Updates</span>
              </div>
            )}
            
            <Button
              onClick={handleRefresh}
              variant="secondary"
              size="small"
              disabled={refreshing || loading}
            >
              {refreshing || loading ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">Refreshing...</span>
                </>
              ) : (
                <>
                  <span className="mr-2">🔄</span>
                  Refresh
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mt-6 border-b border-gray-200">
          <nav className="flex space-x-8">
            {DASHBOARD_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveView(tab.id as DashboardView)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeView === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Active View Content */}
      <div className="dashboard-content">
        {renderActiveView()}
      </div>

      {/* Error Display */}
      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <span className="text-red-500">⚠️</span>
            <span className="text-red-700 text-sm">{error}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedContentQualityDashboard;
